import { and, count, eq, gt, type InitialQueryBuilder, isNull, not } from "@tanstack/db";

import type { WsCommandAlias } from "@storyboard/web-client";
import { store } from "@/store/store";
import type { WorkspaceCommandAlias } from "@/store/types";

export async function bulkUpsertWorkspaceCommandAlias(records: WsCommandAlias[]) {
  if (records.length === 0) return;

  const data = records.map(
    (c): WorkspaceCommandAlias => ({
      id: c.id,
      alias: c.alias,
      workspaceId: c.workspaceId,
      workspaceMembershipId: c.workspaceMembershipId,
      feedId: c.feedId ?? null,
      createdAt: c.createdAt ?? new Date().toISOString(),
    }),
  );

  // The old ON CONFLICT SET kept the first-seen createdAt.
  store.upsert("workspaceCommandAlias", data, (existing, next) => ({
    ...next,
    createdAt: existing.createdAt,
  }));
}

export const multipleWorkspaceAliasInsideFeedQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ alias: store.collection("workspaceCommandAlias") })
    .where(({ alias }) => and(eq(alias.workspaceId, workspaceId), not(isNull(alias.feedId))))
    .groupBy(({ alias }) => alias.feedId)
    .select(({ alias }) => ({ feedId: alias.feedId, aliasFeedCount: count(alias.id) }))
    .having(({ alias }) => gt(count(alias.id), 1));

export const hasAliasChannelsInWorkspaceQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ alias: store.collection("workspaceCommandAlias") })
    .where(({ alias }) => and(eq(alias.workspaceId, workspaceId), not(isNull(alias.feedId))))
    .orderBy(({ alias }) => alias.id)
    .limit(1)
    .select(({ alias }) => ({ feedId: alias.feedId }));

export type FeedCommandAliasRow = { id: string; name: string | null; alias: string; title: string | null };

export const fetchFeedCommandAliasesQuery = (feedId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ alias: store.collection("workspaceCommandAlias") })
    .innerJoin({ membership: store.collection("workspaceMembership") }, ({ alias, membership }) =>
      eq(alias.workspaceMembershipId, membership.id),
    )
    .innerJoin({ account: store.collection("account") }, ({ membership, account }) =>
      eq(membership.accountId, account.id),
    )
    .innerJoin({ feed: store.collection("feed") }, ({ alias, feed }) => eq(alias.feedId, feed.id))
    .where(({ alias }) => eq(alias.feedId, feedId))
    .select(({ alias, account, feed }) => ({
      id: account.id,
      name: account.name,
      alias: alias.alias,
      title: feed.title,
    }));

export type FeedForAccountRow = {
  id: string;
  title: string | null;
  feedType: string;
  isAliasFeed: boolean;
  latestActivity: string | null;
  workspaceId: string | null;
  alias: string;
};

export const fetchFeedsForAccountQuery = (accountId: string, workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ membership: store.collection("workspaceMembership") })
    .innerJoin({ alias: store.collection("workspaceCommandAlias") }, ({ membership, alias }) =>
      eq(membership.id, alias.workspaceMembershipId),
    )
    .innerJoin({ feed: store.collection("feed") }, ({ alias, feed }) => eq(alias.feedId, feed.id))
    // No `not(isNull(alias.feedId))`: the inner join on feed.id already drops null feedIds, and a
    // non-indexable conjunct in the alias where makes TanStack's per-commit join-key snapshot
    // requests fall back from index intersection to a full per-row scan (O(rows × keys)).
    .where(({ membership, alias }) => and(eq(membership.accountId, accountId), eq(alias.workspaceId, workspaceId)))
    .select(({ feed, alias }) => ({
      id: feed.id,
      title: feed.title,
      feedType: feed.feedType,
      isAliasFeed: feed.isAliasFeed,
      latestActivity: feed.latestActivity,
      workspaceId: feed.workspaceId,
      alias: alias.alias,
    }));

/** Distinct alias names on feeds of active alias memberships the caller can write to. */
export const fetchAccessibleAliasKeysQuery =
  (workspaceId: string, workspaceMembershipId: string) => (q: InitialQueryBuilder) =>
    q
      .from({ alias: store.collection("workspaceCommandAlias") })
      .innerJoin({ feed: store.collection("feed") }, ({ alias, feed }) => eq(alias.feedId, feed.id))
      .innerJoin({ aliasMembership: store.collection("workspaceMembership") }, ({ feed, aliasMembership }) =>
        eq(feed.aliasMembershipId, aliasMembership.id),
      )
      .innerJoin({ permission: store.collection("permission") }, ({ feed, permission }) =>
        eq(feed.id, permission.feedId),
      )
      // No `not(isNull(alias.feedId))`: redundant with the inner join on feed.id, and it would make
      // the alias subscription predicate index-inexact (see fetchFeedsForAccountQuery).
      .where(({ alias, aliasMembership, permission }) =>
        and(
          eq(alias.workspaceId, workspaceId),
          eq(aliasMembership.status, "active"),
          eq(permission.workspaceMembershipId, workspaceMembershipId),
          eq(permission.enabled, true),
          eq(permission.name, "write"),
        ),
      )
      .select(({ alias }) => ({ alias: alias.alias }))
      .distinct();

export type WorkspaceCommandAliasRow = {
  id: string;
  alias: string;
  feedId: string | null;
  createdAt: string;
  feedTitle: string | null;
};

export const fetchWorkspaceCommandAliasesQuery =
  (workspaceId: string, workspaceMembershipId: string) => (q: InitialQueryBuilder) =>
    q
      .from({ alias: store.collection("workspaceCommandAlias") })
      .innerJoin({ feed: store.collection("feed") }, ({ alias, feed }) => eq(alias.feedId, feed.id))
      .innerJoin({ permission: store.collection("permission") }, ({ feed, permission }) =>
        eq(feed.id, permission.feedId),
      )
      // No `not(isNull(alias.feedId))`: redundant with the inner join on feed.id, and it would make
      // the alias subscription predicate index-inexact (see fetchFeedsForAccountQuery).
      .where(({ alias, permission }) =>
        and(
          eq(alias.workspaceId, workspaceId),
          eq(permission.workspaceMembershipId, workspaceMembershipId),
          eq(permission.enabled, true),
          eq(permission.name, "write"),
        ),
      )
      .select(({ alias, feed }) => ({
        id: alias.id,
        alias: alias.alias,
        feedId: alias.feedId,
        createdAt: alias.createdAt,
        feedTitle: feed.title,
      }))
      .distinct();
